import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { I18N_SEED } from "@/lib/i18nSeed";

/**
 * App-wide Hindi/English localisation WITHOUT touching every component.
 *
 * Strategy: a controlled DOM translation layer (a robust, React-aware
 * replacement for the flaky Google Translate widget). When the language is
 * Hindi we walk the rendered text nodes, translate them via a persistent
 * Sarvam-backed cache (`/api/i18n/translate`), and observe DOM mutations so
 * lazy-loaded routes / dynamic content get translated too. Switching back to
 * English restores the original text from a per-node map — no reload needed.
 *
 * Default language = Hindi (user requirement). Preference persisted in
 * localStorage. Brand names and elements marked `data-no-translate` /
 * `.notranslate` are never translated.
 */

const LANG_KEY = "mb_lang";
const CACHE_KEY = "mb_i18n_cache_v1";

const I18nCtx = createContext({ lang: "hi", setLang: () => {}, t: (s) => s });
export const useI18n = () => useContext(I18nCtx);

// ---- module-level engine state (survives re-renders) ----
const SKIP_TAGS = new Set([
  "SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG", "PATH", "INPUT", "OPTION",
]);
// Brand tokens we never translate (exact match).
const KEEP = new Set([
  "iBloom", "NetBazzar", "AyuKutumb", "Mapbazar", "MapBazar", "MapBazar.com",
  "MunimJi", "ShopKeeper AI", "WhatsApp", "Aveeno", "Gerber", "AyuKutumb Ai",
]);
const HAS_LATIN = /[A-Za-z]/;
const HAS_DEVANAGARI = /[\u0900-\u097F]/;

function loadCache() {
  let stored = {};
  try { stored = JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); } catch (_) { /* ignore */ }
  return { ...I18N_SEED, ...stored };
}

let CACHE = loadCache();
const ORIGINALS = new Map();      // textNode -> original English string
const PENDING = new Set();        // strings currently being fetched

function persistCache() {
  try {
    // Only persist the runtime-fetched part (seed is bundled).
    const runtime = {};
    for (const k in CACHE) { if (!(k in I18N_SEED)) runtime[k] = CACHE[k]; }
    localStorage.setItem(CACHE_KEY, JSON.stringify(runtime));
  } catch (_) { /* ignore */ }
}

function translatable(raw) {
  const s = (raw || "").trim();
  if (s.length < 2) return false;
  if (!HAS_LATIN.test(s)) return false;        // numbers / emoji / symbols only
  if (HAS_DEVANAGARI.test(s)) return false;    // already (partly) Hindi
  if (KEEP.has(s)) return false;               // brand token
  if (/^[\d\s.,:/%+\-₹$€]+$/.test(s)) return false; // pure numeric-ish
  return true;
}

function isSkipped(node) {
  let el = node.parentElement;
  while (el) {
    if (SKIP_TAGS.has(el.tagName)) return true;
    if (el.hasAttribute && (el.hasAttribute("data-no-translate") || el.classList.contains("notranslate"))) return true;
    el = el.parentElement;
  }
  return false;
}

/** Walk #root text nodes; apply cached Hindi, collect misses. Returns missing[]. */
function applyHindi() {
  const root = document.getElementById("root") || document.body;
  const missing = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const raw = node.nodeValue;
      if (!raw || !raw.trim()) return NodeFilter.FILTER_REJECT;
      if (isSkipped(node)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node;
  while ((node = walker.nextNode())) {
    const raw = node.nodeValue;
    const key = raw.trim();
    if (!translatable(key)) continue;
    // keep ORIGINALS fresh with the latest English (React may re-render values)
    ORIGINALS.set(node, raw);
    const hi = CACHE[key];
    if (hi) {
      const next = raw.replace(key, hi);
      if (node.nodeValue !== next) node.nodeValue = next;
    } else if (!PENDING.has(key)) {
      missing.push(key);
    }
  }
  return missing;
}

function restoreEnglish() {
  ORIGINALS.forEach((orig, node) => {
    try { if (node.nodeValue !== orig) node.nodeValue = orig; } catch (_) { /* node gone */ }
  });
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) || "hi"; } catch (_) { return "hi"; }
  });
  const observerRef = useRef(null);
  const rafRef = useRef(0);
  const applyingRef = useRef(false);

  const fetchMissing = useCallback(async (missing) => {
    const items = missing.filter((k) => !PENDING.has(k) && !CACHE[k]);
    if (!items.length) return;
    items.forEach((k) => PENDING.add(k));
    for (let i = 0; i < items.length; i += 50) {
      const chunk = items.slice(i, i + 50);
      try {
        const res = await api.post("/i18n/translate", { texts: chunk, target: "hi" });
        const tr = (res.data && res.data.translations) || {};
        let got = false;
        for (const k in tr) { CACHE[k] = tr[k]; got = true; }
        if (got) { persistCache(); scheduleApply(); }
      } catch (_) { /* leave English fallback */ }
      finally { chunk.forEach((k) => PENDING.delete(k)); }
    }
  }, []);

  const runApply = useCallback(() => {
    if (applyingRef.current) return;
    applyingRef.current = true;
    const obs = observerRef.current;
    if (obs) obs.disconnect();
    let missing = [];
    try { missing = applyHindi(); } catch (_) { /* ignore */ }
    if (obs) obs.observe(document.getElementById("root") || document.body, {
      childList: true, subtree: true, characterData: true,
    });
    applyingRef.current = false;
    if (missing.length) fetchMissing(missing);
  }, [fetchMissing]);

  const scheduleApply = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setTimeout(runApply, 60));
  }, [runApply]);

  // Engine lifecycle: attach observer + translate when Hindi; restore on English.
  useEffect(() => {
    const root = document.getElementById("root") || document.body;
    document.documentElement.setAttribute("lang", lang === "hi" ? "hi" : "en");

    if (lang === "hi") {
      const obs = new MutationObserver(() => { if (!applyingRef.current) scheduleApply(); });
      observerRef.current = obs;
      obs.observe(root, { childList: true, subtree: true, characterData: true });
      runApply();
      return () => { obs.disconnect(); observerRef.current = null; cancelAnimationFrame(rafRef.current); };
    }
    // English: stop observing + restore originals.
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    restoreEnglish();
    return undefined;
  }, [lang, runApply, scheduleApply]);

  const setLang = useCallback((l) => {
    const next = l === "en" ? "en" : "hi";
    try { localStorage.setItem(LANG_KEY, next); } catch (_) { /* ignore */ }
    // Reload so the switch is bulletproof: English is the source language (renders
    // natively, no observer), Hindi re-runs the translator from a clean tree.
    if (typeof window !== "undefined") window.location.reload();
    else setLangState(next);
  }, []);

  const t = useCallback((s) => {
    if (lang !== "hi" || !s) return s;
    const key = String(s).trim();
    return CACHE[key] || s;
  }, [lang]);

  return (
    <I18nCtx.Provider value={{ lang, setLang, t }}>
      {children}
      <LanguageToggle lang={lang} setLang={setLang} />
    </I18nCtx.Provider>
  );
}

function LanguageToggle({ lang, setLang }) {
  const other = lang === "hi" ? "en" : "hi";
  return (
    <button
      data-no-translate
      className="notranslate"
      data-testid={other === "en" ? "lang-toggle-en" : "lang-toggle-hi"}
      onClick={() => setLang(other)}
      aria-label={other === "en" ? "Switch to English" : "हिंदी में बदलें"}
      title={other === "en" ? "English" : "हिंदी"}
      style={{
        position: "fixed", top: 10, right: 10, zIndex: 4000,
        width: 34, height: 34, borderRadius: "50%",
        border: "1px solid #E2DFD2", background: "rgba(255,255,255,0.96)",
        backdropFilter: "blur(8px)", color: "#0F3D2E",
        fontSize: 11.5, fontWeight: 800, cursor: "pointer", padding: 0,
        boxShadow: "0 3px 10px rgba(0,0,0,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >{other === "en" ? "EN" : "हिं"}</button>
  );
}
